import { useEffect, useState } from 'react';
import { Users, UserPlus, RefreshCw, AlertCircle, CheckCircle, X } from 'lucide-react';
import {
  Operator,
  inviteOperator,
  listOperators,
  updateOperator,
  resendInvite,
} from '../lib/api/operators.js';
import { useAuth } from '../contexts/AuthContext.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/ui/modal.js';
import { TableScroll } from '../components/ui/table-scroll.js';

const AVAILABLE_ROLES = ['super_admin', 'social_manager', 'sales', 'data_manager'];

interface FlashMessage {
  kind: 'success' | 'error';
  text: string;
}

export function AdminOperatorsPage() {
  const { operator: me } = useAuth();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState<FlashMessage | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listOperators();
      setOperators(data.operators);
    } catch (err: any) {
      setFlash({ kind: 'error', text: err?.message || 'Failed to load operators.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleResendInvite = async (op: Operator) => {
    if (!op.email) {
      setFlash({ kind: 'error', text: 'Operator has no email on record.' });
      return;
    }
    try {
      await resendInvite(op.firebase_uid);
      setFlash({ kind: 'success', text: `Invite email re-sent to ${op.email}.` });
    } catch (err: any) {
      setFlash({ kind: 'error', text: err?.response?.data?.detail || err?.message || 'Failed to send invite email.' });
    }
  };

  const handleToggleStatus = async (op: Operator) => {
    const next = op.status === 'active' ? 'disabled' : 'active';
    try {
      const updated = await updateOperator(op.firebase_uid, { status: next });
      setOperators((rows) => rows.map((r) => (r.firebase_uid === op.firebase_uid ? updated : r)));
      setFlash({ kind: 'success', text: `Operator ${updated.email} is now ${updated.status}.` });
    } catch (err: any) {
      setFlash({
        kind: 'error',
        text: err?.response?.data?.detail || err?.message || 'Failed to update status.',
      });
    }
  };

  const handleRolesChange = async (op: Operator, roles: string[]) => {
    try {
      const updated = await updateOperator(op.firebase_uid, { roles });
      setOperators((rows) => rows.map((r) => (r.firebase_uid === op.firebase_uid ? updated : r)));
      setFlash({ kind: 'success', text: `Roles updated for ${updated.email}.` });
    } catch (err: any) {
      setFlash({
        kind: 'error',
        text: err?.response?.data?.detail || err?.message || 'Failed to update roles.',
      });
    }
  };

  const handleToggleModerationAlerts = async (op: Operator) => {
    try {
      const updated = await updateOperator(op.firebase_uid, {
        receives_moderation_alerts: !op.receives_moderation_alerts,
      });
      setOperators((rows) => rows.map((r) => (r.firebase_uid === op.firebase_uid ? updated : r)));
      setFlash({
        kind: 'success',
        text: `Moderation alerts ${updated.receives_moderation_alerts ? 'enabled' : 'disabled'} for ${updated.email}.`,
      });
    } catch (err: any) {
      setFlash({
        kind: 'error',
        text: err?.response?.data?.detail || err?.message || 'Failed to update moderation alerts.',
      });
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Users className="h-7 w-7 text-grey-700" />
          <h1 className="text-2xl sm:text-3xl font-bold text-grey-700">Operators</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={load}
            className="flex items-center gap-2 px-4 py-2 text-grey-700 border border-grey-200 rounded-lg hover:bg-grey-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setInviteOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-04 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            Invite Operator
          </button>
        </div>
      </div>

      {flash && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${
            flash.kind === 'success'
              ? 'bg-success-50 border border-success-200 text-success-600'
              : 'bg-danger-50 border border-danger-200 text-danger-600'
          }`}
        >
          {flash.kind === 'success' ? (
            <CheckCircle className="h-4 w-4 mt-0.5" />
          ) : (
            <AlertCircle className="h-4 w-4 mt-0.5" />
          )}
          <span className="text-sm flex-1">{flash.text}</span>
          <button onClick={() => setFlash(null)} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <TableScroll className="bg-surface rounded-lg border border-grey-200">
        <table className="w-full text-sm">
          <thead className="bg-grey-50 border-b border-grey-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-grey-700">Email</th>
              <th className="text-left px-4 py-3 font-medium text-grey-700">Display Name</th>
              <th className="text-left px-4 py-3 font-medium text-grey-700">Roles</th>
              <th className="text-left px-4 py-3 font-medium text-grey-700">Status</th>
              <th className="text-left px-4 py-3 font-medium text-grey-700">Moderation alerts</th>
              <th className="text-right px-4 py-3 font-medium text-grey-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-grey-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-grey-500">
                  Loading…
                </td>
              </tr>
            ) : operators.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-grey-500">
                  No operators.
                </td>
              </tr>
            ) : (
              operators.map((op) => (
                <OperatorRow
                  key={op.firebase_uid}
                  op={op}
                  isMe={me?.firebase_uid === op.firebase_uid}
                  onResend={() => handleResendInvite(op)}
                  onToggleStatus={() => handleToggleStatus(op)}
                  onRolesChange={(roles) => handleRolesChange(op, roles)}
                  onToggleModerationAlerts={() => handleToggleModerationAlerts(op)}
                />
              ))
            )}
          </tbody>
        </table>
      </TableScroll>

      {inviteOpen && (
        <InviteOperatorModal
          onClose={() => setInviteOpen(false)}
          onInvited={(op) => {
            setOperators((rows) => [op, ...rows]);
            setFlash({
              kind: 'success',
              text: `Invited ${op.email}. Password-reset email sent.`,
            });
            setInviteOpen(false);
          }}
          setFlash={setFlash}
        />
      )}
    </div>
  );
}

function OperatorRow({
  op,
  isMe,
  onResend,
  onToggleStatus,
  onRolesChange,
  onToggleModerationAlerts,
}: {
  op: Operator;
  isMe: boolean;
  onResend: () => void;
  onToggleStatus: () => void;
  onRolesChange: (roles: string[]) => void;
  onToggleModerationAlerts: () => void;
}) {
  const [editingRoles, setEditingRoles] = useState(false);
  const [draftRoles, setDraftRoles] = useState<string[]>(op.roles);

  const startEdit = () => {
    setDraftRoles(op.roles);
    setEditingRoles(true);
  };

  const toggleRole = (r: string) => {
    setDraftRoles((current) =>
      current.includes(r) ? current.filter((x) => x !== r) : [...current, r],
    );
  };

  const save = () => {
    onRolesChange(draftRoles);
    setEditingRoles(false);
  };

  return (
    <tr>
      <td className="px-4 py-3 text-grey-700 font-medium">
        {op.email ?? <span className="italic text-grey-500">no email</span>}
        {isMe && <span className="ml-2 text-xs text-grey-500">(you)</span>}
      </td>
      <td className="px-4 py-3 text-grey-700">{op.display_name ?? '—'}</td>
      <td className="px-4 py-3">
        {editingRoles ? (
          <div className="flex flex-wrap gap-2 items-center">
            {AVAILABLE_ROLES.map((r) => (
              <label key={r} className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={draftRoles.includes(r)}
                  onChange={() => toggleRole(r)}
                />
                {r}
              </label>
            ))}
            <button
              onClick={save}
              className="px-2 py-1 bg-primary-04 text-white text-xs rounded hover:bg-primary-600"
            >
              Save
            </button>
            <button
              onClick={() => setEditingRoles(false)}
              className="px-2 py-1 text-xs text-grey-500 hover:text-grey-700"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {op.roles.length === 0 ? (
              <span className="text-grey-500 italic text-xs">none</span>
            ) : (
              op.roles.map((r) => (
                <span
                  key={r}
                  className="px-2 py-0.5 bg-primary-50 text-primary-700 text-xs font-medium rounded"
                >
                  {r}
                </span>
              ))
            )}
            <button
              onClick={startEdit}
              className="px-2 py-0.5 text-xs text-primary-04 hover:text-primary-600"
            >
              Edit
            </button>
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded ${
            op.status === 'active'
              ? 'bg-success-50 text-success-600'
              : 'bg-grey-100 text-grey-500'
          }`}
        >
          {op.status}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleModerationAlerts}
            role="switch"
            aria-checked={op.receives_moderation_alerts}
            title={
              op.receives_moderation_alerts
                ? 'Receiving article-held-for-review emails — click to stop'
                : 'Not receiving moderation emails — click to start'
            }
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-04 focus:ring-offset-1 ${
              op.receives_moderation_alerts
                ? 'bg-primary-04 border-primary-04'
                : 'bg-grey-200 border-grey-400'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow transition-transform ${
                op.receives_moderation_alerts ? 'translate-x-4' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span className="text-xs text-grey-500">
            {op.receives_moderation_alerts ? 'On' : 'Off'}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex gap-2">
          <button
            onClick={onResend}
            className="px-2 py-1 text-xs text-grey-700 border border-grey-200 rounded hover:bg-grey-50"
          >
            Resend invite
          </button>
          <button
            onClick={onToggleStatus}
            disabled={isMe}
            title={isMe ? "You can't disable your own account." : undefined}
            className="px-2 py-1 text-xs text-grey-700 border border-grey-200 rounded hover:bg-grey-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {op.status === 'active' ? 'Disable' : 'Re-enable'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function InviteOperatorModal({
  onClose,
  onInvited,
  setFlash,
}: {
  onClose: () => void;
  onInvited: (op: Operator) => void;
  setFlash: (msg: FlashMessage) => void;
}) {
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>(['social_manager']);
  const [useInitialPassword, setUseInitialPassword] = useState(false);
  const [initialPassword, setInitialPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleRole = (r: string) => {
    setRoles((current) =>
      current.includes(r) ? current.filter((x) => x !== r) : [...current, r],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError('Email is required.');
      return;
    }
    if (useInitialPassword && initialPassword.length < 8) {
      setError('Initial password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const newOp = await inviteOperator({
        email: email.trim().toLowerCase(),
        roles,
        initial_password: useInitialPassword ? initialPassword : undefined,
      });
      if (useInitialPassword) {
        // Operator-set password: backend sends no email. Admin shares creds.
        setFlash({
          kind: 'success',
          text: `Invited ${newOp.email}. Share the password with them directly — no email sent.`,
        });
      } else {
        // Backend generated the link + sent the branded invite via Mailjet.
        setFlash({
          kind: 'success',
          text: `Invited ${newOp.email} — a set-password email has been sent. Use "Resend invite" if it doesn't arrive.`,
        });
      }
      onInvited(newOp);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to invite operator.');
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} canClose={!submitting} size="md">
      <ModalHeader>
        <div className="flex items-center">
          <UserPlus className="h-5 w-5 text-primary-04 mr-2" />
          <h2 className="text-xl font-semibold text-grey-700">Invite Operator</h2>
        </div>
        <button onClick={onClose} aria-label="Close" disabled={submitting} className="touch-target">
          <X className="h-5 w-5 text-grey-500 hover:text-grey-700" />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <ModalBody className="space-y-4">
          {error && (
            <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
              <p className="text-danger-600 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="invite-email" className="block text-sm font-medium text-grey-700 mb-1">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent"
              placeholder="person@example.com"
              autoFocus
              disabled={submitting}
            />
          </div>

          <div>
            <p className="block text-sm font-medium text-grey-700 mb-2">Roles</p>
            <div className="flex flex-col gap-2">
              {AVAILABLE_ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm text-grey-700">
                  <input
                    type="checkbox"
                    checked={roles.includes(r)}
                    onChange={() => toggleRole(r)}
                    disabled={submitting}
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>

          {/* Optional explicit initial password — for the "domain customisation
              not ready yet" path. When ticked, no reset email is sent; admin
              communicates the password out-of-band. */}
          <div className="border border-grey-200 rounded-lg p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-grey-700">
              <input
                type="checkbox"
                checked={useInitialPassword}
                onChange={(e) => setUseInitialPassword(e.target.checked)}
                disabled={submitting}
              />
              Set an initial password (skip the reset-link email)
            </label>
            {useInitialPassword && (
              <input
                type="text"
                required
                minLength={8}
                value={initialPassword}
                onChange={(e) => setInitialPassword(e.target.value)}
                className="w-full px-3 py-2 border border-grey-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-04 focus:border-transparent font-mono"
                placeholder="At least 8 characters"
                disabled={submitting}
                autoComplete="off"
              />
            )}
          </div>

          <p className="text-xs text-grey-500">
            {useInitialPassword
              ? 'No email will be sent. Share the password with the operator directly.'
              : 'The operator will receive an email from Firebase with a link to set their password.'}
          </p>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-grey-700 border border-grey-200 rounded-lg hover:bg-grey-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !email}
            className="px-4 py-2 bg-primary-04 text-white font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {submitting ? 'Inviting…' : 'Send Invite'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
